import { statusOf } from '../api/problem';
import { pastaApi, useGetExecutionQuery, useStartExecutionMutation, type Execution } from '../api/generated';
import { isFinished } from './executionText';

/** How often an execution in progress is checked (quicker under test). */
export const EXECUTION_POLL_MS = import.meta.env.MODE === 'test' ? 50 : 500;

export interface ExecutionState {
  /** The paste's execution, finished or in progress; undefined while unknown, never run or gone. */
  readonly execution: Execution | undefined;
  /** The paste has not been executed yet, so it can be. */
  readonly notRun: boolean;
  /** The execution disappeared, i.e. its paste was deleted. */
  readonly gone: boolean;
  readonly loading: boolean;
  /** Starting (or reading) failed for a reason other than "not run yet". */
  readonly error: ReturnType<typeof useGetExecutionQuery>['error'];
  readonly starting: boolean;
  readonly run: () => Promise<void>;
}

/**
 * A paste's single execution: whether it ran, its result, and a way to start it. Polls while the
 * execution is in progress. Does nothing for pastes that cannot be executed.
 */
export function useExecution(pasteId: string, executable: boolean): ExecutionState {
  // What is cached decides whether to keep polling: only while the execution is in progress, and
  // not after a failed read (RTK Query keeps the last data then, which may say "running").
  const cached = pastaApi.endpoints.getExecution.useQueryState({ pasteId }, { skip: !executable });
  const inProgress = !cached.isError && cached.data !== undefined && !isFinished(cached.data);
  const query = useGetExecutionQuery({ pasteId }, { skip: !executable, pollingInterval: inProgress ? EXECUTION_POLL_MS : 0 });
  const [startExecution, start] = useStartExecutionMutation();
  const notFound = statusOf(query.error) === 404;
  const hadExecution = query.data !== undefined;

  return {
    execution: notFound ? undefined : query.data,
    // while a start is answered and the execution re-read, it is not "not run" any more
    notRun: executable && notFound && !hadExecution && !query.isFetching && !start.isLoading,
    gone: notFound && hadExecution,
    loading: query.isLoading,
    error: start.error ?? (notFound ? undefined : query.error),
    starting: start.isLoading || (notFound && query.isFetching),
    run: async () => {
      await startExecution({ pasteId }); // refreshes the execution query (shared tag)
    },
  };
}
